import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import type { Specification } from '../Spec/SpecDataList.tsx';
import type { ComparisonResult } from './Step3Panel.tsx';
import {
  createReleaseCompareRowsByBatch,
  deleteReleaseCompareRowsByBatch,
  getExperimentResultItems,
  getExperiments,
  getReleaseCompareRowsByBatch,
  getReleaseTasks,
  getReleaseMatchRows,
  getSpecifications,
  saveReleaseParameterMappings,
  type ExperimentResultItemDto,
} from '../../../services/coaReleaseService.ts';
import {
  buildMatchOptionLabel,
  convertTestValueRawToUnit,
  evaluateTestValueAgainstSpec,
  extractBranchRank,
  normalizeComparableText,
  sortMatchOptions,
  toDisplayText,
  toNumber,
} from './releaseUtils.ts';

const MATCH_RECOMMENDATION_THRESHOLD = 30;

export interface MatchOption {
  id: string;
  label: string;
  analysis: string;
  analysisVariation?: string;
  testValueRaw: string;
  unit: string;
  score: number;
  branchRank: number;
}

export interface Step2Row {
  rowKey: string;
  parameter: string;
  testMethod: string;
  specMinRaw: number | string | null;
  specMaxRaw: number | string | null;
  specUnit: string;
  matchOptions: MatchOption[];
  selectedMatchId: string | null;
}

const buildMatchOptionSearchText = (
  option: Pick<MatchOption, 'label' | 'analysis' | 'analysisVariation' | 'testValueRaw' | 'unit'>
) =>
  [option.label, option.analysis, option.analysisVariation, option.testValueRaw, option.unit]
    .filter(Boolean)
    .join(' ');

interface MatchOptionComboboxProps {
  options: MatchOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

function MatchOptionCombobox({ options, value, onChange }: MatchOptionComboboxProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = normalizeComparableText(searchText);
    if (!normalizedSearch) return options;
    return options.filter((option) =>
      normalizeComparableText(buildMatchOptionSearchText(option)).includes(normalizedSearch)
    );
  }, [options, searchText]);

  const unmatchOptionVisible = useMemo(() => {
    const normalizedSearch = normalizeComparableText(searchText);
    return !normalizedSearch || 'unmatch'.includes(normalizedSearch);
  }, [searchText]);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between font-normal"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="truncate text-left">
          {selectedOption ? `[${selectedOption.score}] ${selectedOption.label}` : '[0] Unmatch'}
        </span>
        <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 w-full rounded-md border border-gray-200 bg-white p-2 shadow-lg">
          <input
            autoFocus
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            placeholder="Search by experiment name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-100">
            {unmatchOptionVisible && (
              <button
                type="button"
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${value === null ? 'bg-blue-50' : ''}`}
                onClick={() => { onChange(null); setOpen(false); setSearchText(''); }}
              >
                <Check className={`mr-2 size-4 ${value === null ? 'opacity-100' : 'opacity-0'}`} />
                <span className="truncate text-left">[0] Unmatch</span>
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">No matching experiment found.</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.id === value;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    onClick={() => { onChange(option.id); setOpen(false); setSearchText(''); }}
                  >
                    <Check className={`mr-2 size-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate text-left">{`[${option.score}] ${option.label}`}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface Step2PanelProps {
  selectedBatch: string;
  isRetestMode: boolean;
  onBack: () => void;
  onApplySuccess: (results: ComparisonResult[]) => void;
  onLoadingChange: (loading: boolean) => void;
  onErrorChange: (error: string) => void;
}

export default function Step2Panel({
  selectedBatch,
  isRetestMode,
  onBack,
  onApplySuccess,
  onLoadingChange,
  onErrorChange,
}: Step2PanelProps) {
  const [step2Rows, setStep2Rows] = useState<Step2Row[]>([]);

  const matchedCount = useMemo(
    () => step2Rows.filter((row) => row.selectedMatchId !== null).length,
    [step2Rows]
  );

  const buildComparisonRows = useCallback((rows: Step2Row[], batchNumber: string): ComparisonResult[] => {
    return rows
      .filter((row) => row.selectedMatchId)
      .map((row) => {
        const match = row.matchOptions.find((item) => item.id === row.selectedMatchId) || null;
        const rawTestValue = match?.testValueRaw || '';
        const alignedTestValue = convertTestValueRawToUnit(rawTestValue, match?.unit || '', row.specUnit || '');
        const testValueRaw = alignedTestValue.valueRaw;
        const evaluation = evaluateTestValueAgainstSpec(testValueRaw, row.specMinRaw, row.specMaxRaw);
        return {
          batchNumber,
          parameter: row.parameter,
          testMethod: row.testMethod,
          uom: row.specUnit || '',
          analysis: match?.analysis || '',
          specMin: toNumber(row.specMinRaw),
          specMax: toNumber(row.specMaxRaw),
          specMinDisplay: toDisplayText(row.specMinRaw),
          specMaxDisplay: toDisplayText(row.specMaxRaw),
          specUnit: row.specUnit || null,
          testValue: evaluation.numericValue,
          testValueRaw,
          units: alignedTestValue.unit || match?.unit || '',
          passed: evaluation.passed,
          reason: evaluation.reason,
        };
      });
  }, []);

  const clearCompareRowsForBatch = useCallback(async (batchNumber: string) => {
    const existingRows = await getReleaseCompareRowsByBatch(batchNumber);
    if (existingRows.length > 0) {
      await deleteReleaseCompareRowsByBatch(
        existingRows.map((item) => Number(item.id)).filter((id) => Number.isFinite(id))
      );
    }
  }, []);

  const saveCompareRowsForBatch = useCallback(async (batchNumber: string, rows: ComparisonResult[]) => {
    await clearCompareRowsForBatch(batchNumber);
    const payloadItems = rows.map((item) => ({
      BatchNumber: batchNumber,
      Parameter: item.parameter,
      TestMethod: item.testMethod || '',
      Uom: item.uom || '',
      Analysis: item.analysis || '',
      Units: item.units || '',
      SpecMin: item.specMinDisplay || '',
      SpecMax: item.specMaxDisplay || '',
      TestValue: item.testValueRaw || String(item.testValue ?? ''),
      TestResult: item.passed ? 'passed' : 'failed',
    }));
    await createReleaseCompareRowsByBatch(payloadItems);
  }, [clearCompareRowsForBatch]);

  const fetchCompareRowsByBatch = useCallback(async (batchNumber: string): Promise<ComparisonResult[]> => {
    const compareItems = await getReleaseCompareRowsByBatch(batchNumber);
    return compareItems.map((item) => {
      const testValueRaw = String(item.testValue || '');
      const unitAlignment = convertTestValueRawToUnit(testValueRaw, item.units || '', item.uom || '');
      const evaluation = evaluateTestValueAgainstSpec(unitAlignment.valueRaw, item.specMin, item.specMax);
      return {
        batchNumber: item.batchNumber || batchNumber,
        parameter: item.parameter || '-',
        testMethod: item.testMethod || '-',
        uom: item.uom || '-',
        analysis: item.analysis || '-',
        specMin: toNumber(item.specMin),
        specMax: toNumber(item.specMax),
        specMinDisplay: String(item.specMin || ''),
        specMaxDisplay: String(item.specMax || ''),
        specUnit: item.uom || null,
        testValue: evaluation.numericValue,
        testValueRaw: unitAlignment.valueRaw,
        units: unitAlignment.unit || item.units || '',
        passed: evaluation.passed,
        reason: evaluation.passed ? '' : evaluation.reason,
      };
    });
  }, []);

  const applyBackendMatching = useCallback(async (batchNumber: string, rows: Step2Row[]): Promise<Step2Row[] | null> => {
    const payload = {
      batchNumber,
      rows: rows.map((row) => ({
        rowKey: row.rowKey,
        parameter: row.parameter,
        testMethod: row.testMethod,
        options: row.matchOptions.map((option) => ({
          id: option.id,
          analysis: option.analysis,
          score: option.score,
        })),
      })),
    };

    const backendRows = await getReleaseMatchRows(payload);
    if (!backendRows || backendRows.length === 0) return null;

    return rows.map((row) => {
      const backendRow =
        backendRows.find((item) => item.rowKey === row.rowKey) ||
        backendRows.find(
          (item) => normalizeComparableText(item.parameter || '') === normalizeComparableText(row.parameter || '')
        );

      if (!backendRow) return row;

      const backendOptions = backendRow.options || [];
      const mergedOptions = row.matchOptions.map((option) => {
        const fromBackend = backendOptions.find((item) => item.id === option.id);
        const merged = fromBackend
          ? {
              ...option,
              analysis: fromBackend.analysis ?? option.analysis,
              unit: fromBackend.unit ?? option.unit,
              testValueRaw: fromBackend.testValueRaw ?? option.testValueRaw,
              score: typeof fromBackend.score === 'number' ? fromBackend.score : option.score,
            }
          : option;
        return { ...merged, label: buildMatchOptionLabel(merged) };
      });

      const sortedOptions = sortMatchOptions(mergedOptions);
      const bestOption = sortedOptions[0] || null;
      const selectedMatchId =
        bestOption && bestOption.score >= MATCH_RECOMMENDATION_THRESHOLD ? bestOption.id : null;

      return { ...row, matchOptions: sortedOptions, selectedMatchId };
    });
  }, []);

  useEffect(() => {
    const batchNumber = selectedBatch.trim();
    if (!batchNumber) return;

    const load = async () => {
      onLoadingChange(true);
      onErrorChange('');
      try {
        if (!isRetestMode) {
          const params = new URLSearchParams();
          params.set('range', '[0,9999]');
          params.set('perPage', '10000');
          params.set('filter', JSON.stringify({ batchNumber }));
          const existing = await getReleaseTasks({
            range: '[0,9999]',
            perPage: '10000',
            filter: params.get('filter') || '',
          });
          const found = existing.find(
            (item) => normalizeComparableText(String(item.batchNumber || '')) === normalizeComparableText(batchNumber)
          );
          if (found) {
            throw new Error(`Batch ${batchNumber} already has an existing release task.`);
          }
        }

        const [specList, experiments] = await Promise.all([
          getSpecifications({ range: '[0,999]', perPage: '1000', filter: JSON.stringify({ batchNumber }) }) as Promise<Specification[]>,
          getExperiments({ range: '[0,500]', perPage: '500', filter: JSON.stringify({ batchNumber }) }),
        ]);

        const exactSpecs = specList.filter((item) => String(item.batchNumber || '').trim() === batchNumber);
        if (exactSpecs.length === 0) throw new Error('No specification records found for selected batch.');

        const exactExperiments = experiments.filter((item) => String(item.batchNumber || '').trim() === batchNumber);
        if (exactExperiments.length === 0) throw new Error('No experiment records found for selected batch.');

        const allExperimentItems = (
          await Promise.all(
            exactExperiments.map(async (exp) => {
              try {
                return await getExperimentResultItems({
                  range: '[0,2000]',
                  perPage: '2000',
                  filter: JSON.stringify({ experimentId: exp.id }),
                });
              } catch {
                return [] as ExperimentResultItemDto[];
              }
            })
          )
        ).flat();

        const candidates: MatchOption[] = allExperimentItems.map((item) => {
          const sample = String((item as any).sampleResult || '').trim();
          const analysisRaw = String(item.analysis || '').trim();
          const analysis = `${sample} ${analysisRaw}`.trim();
          const analysisVariation = String(item.analysisVariation || '').trim();
          const testValueRaw = String(item.testValue || '').trim();
          const unit = String(item.unit || '').trim();
          const option = {
            id: `${item.experimentId}:${item.id}`,
            label: '',
            analysis,
            analysisVariation,
            testValueRaw,
            unit,
            score: 0,
            branchRank: extractBranchRank(analysisVariation, analysis, String(item.id || '')),
          };
          return { ...option, label: buildMatchOptionLabel(option) };
        });

        const rows = exactSpecs.flatMap((spec, specIndex) =>
          (spec.items || []).map((item, itemIndex) => ({
            rowKey: `${spec.id}-${specIndex}-${itemIndex}`,
            parameter: item.parameter || '-',
            testMethod: item.testMethod || '-',
            specMinRaw: item.specMin,
            specMaxRaw: item.specMax,
            specUnit: item.uom || '',
            matchOptions: candidates,
            selectedMatchId: null,
          } as Step2Row))
        );

        const backendMatchedRows = await applyBackendMatching(batchNumber, rows);
        if (!backendMatchedRows) throw new Error('Backend matching returned no result.');

        backendMatchedRows.sort((a, b) => {
          const aMatched = a.selectedMatchId ? 1 : 0;
          const bMatched = b.selectedMatchId ? 1 : 0;
          if (aMatched !== bMatched) return bMatched - aMatched;
          return a.parameter.localeCompare(b.parameter);
        });

        setStep2Rows(backendMatchedRows);
      } catch (error) {
        onErrorChange(error instanceof Error ? error.message : 'Load step 2 data failed.');
        setStep2Rows([]);
      } finally {
        onLoadingChange(false);
      }
    };

    void load();
  }, [selectedBatch, isRetestMode, applyBackendMatching, clearCompareRowsForBatch, onLoadingChange, onErrorChange]);

  const handleApplyStep2 = useCallback(async () => {
    const batchNumber = selectedBatch.trim();
    if (!isRetestMode && !batchNumber) {
      onErrorChange('Batch Number is required.');
      return;
    }

    const calculatedRows = buildComparisonRows(step2Rows, batchNumber);
    if (calculatedRows.length === 0) {
      onErrorChange('No comparison rows to save.');
      return;
    }

    onLoadingChange(true);
    onErrorChange('');
    try {
      const mappingsToSave = step2Rows.map((row) => {
        const selectedOption = row.matchOptions.find((option) => option.id === row.selectedMatchId) || null;
        return {
          rowKey: row.rowKey,
          parameter: row.parameter,
          analysis: selectedOption?.analysis || 'unmatch',
          selectedMatchId: row.selectedMatchId || '',
          testMethod: row.testMethod,
          uom: row.specUnit,
          unit: selectedOption?.unit || '',
        };
      });

      const selectedMappings = mappingsToSave.filter((row) => row.selectedMatchId);
      if (selectedMappings.length === 0) throw new Error('No matched analysis selected.');

      await saveReleaseParameterMappings({ batchNumber, mappings: mappingsToSave });
      if (!isRetestMode) {
        // In create mode save compare rows immediately so they're visible in step 3
        await saveCompareRowsForBatch(batchNumber, calculatedRows);
        const savedRows = await fetchCompareRowsByBatch(batchNumber);
        onApplySuccess(savedRows);
      } else {
        // In retest mode keep the previous DB results intact until the user confirms Save
        onApplySuccess(calculatedRows);
      }
    } catch (error) {
      onErrorChange(error instanceof Error ? error.message : 'Save step 2 matching failed.');
    } finally {
      onLoadingChange(false);
    }
  }, [
    selectedBatch,
    isRetestMode,
    step2Rows,
    buildComparisonRows,
    saveCompareRowsForBatch,
    fetchCompareRowsByBatch,
    onApplySuccess,
    onLoadingChange,
    onErrorChange,
  ]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <h3 className="text-lg">Step 2: Confirmate Specifications</h3>
          <p className="text-sm text-gray-500">batch number:{selectedBatch}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 mr-2">Matched {matchedCount} / {step2Rows.length}</span>
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            type="button"
            disabled={matchedCount === 0}
            onClick={() => { void handleApplyStep2(); }}
          >
            Next
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-left">Spec Name</th>
              <th className="px-3 py-2 text-left">Test Method</th>
              <th className="px-3 py-2 text-left">UOM</th>
              <th className="px-3 py-2 text-left">Experiment Name</th>
              <th className="px-3 py-2 text-left">Unit</th>
            </tr>
          </thead>
          <tbody>
            {step2Rows.map((row) => {
              const selected = row.matchOptions.find((item) => item.id === row.selectedMatchId) || null;
              const hasMatch = !!row.selectedMatchId;
              const availableOptions = sortMatchOptions(row.matchOptions);
              return (
                <tr key={row.rowKey} className={hasMatch ? 'bg-green-50 border-b border-gray-100' : 'bg-white border-b border-gray-100'}>
                  <td className="px-3 py-2">{row.parameter}</td>
                  <td className="px-3 py-2">{row.testMethod || '-'}</td>
                  <td className="px-3 py-2">{row.specUnit || '-'}</td>
                  <td className="px-3 py-2 min-w-[360px]">
                    <MatchOptionCombobox
                      options={availableOptions}
                      value={row.selectedMatchId}
                      onChange={(value) => {
                        setStep2Rows((prev) =>
                          prev.map((item) =>
                            item.rowKey === row.rowKey ? { ...item, selectedMatchId: value } : item
                          )
                        );
                      }}
                    />
                  </td>
                  <td className="px-3 py-2">{selected?.unit || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
